import { useState } from 'react'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import DialogTitle from '@mui/joy/DialogTitle'
import DialogContent from '@mui/joy/DialogContent'
import DialogActions from '@mui/joy/DialogActions'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import Typography from '@mui/joy/Typography'
import Stack from '@mui/joy/Stack'
import { CrmCompany } from 'api/oracleApi'

interface Props {
    open: boolean
    source: CrmCompany | null
    candidates: CrmCompany[]
    onMerge: (fromId: string, intoId: string) => Promise<void>
    onClose: () => void
}

export function MergeModal({ open, source, candidates, onMerge, onClose }: Props) {
    const [query, setQuery] = useState('')
    const [selectedId, setSelectedId] = useState('')
    const [loading, setLoading] = useState(false)

    const filtered = candidates.filter((c) => c.id !== source?.id && c.name_display.toLowerCase().includes(query.toLowerCase()))

    const handleMerge = async () => {
        if (!source || !selectedId) return
        setLoading(true)
        try {
            await onMerge(source.id, selectedId)
            onClose()
        } finally {
            setLoading(false)
            setQuery('')
            setSelectedId('')
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog minWidth={480}>
                <ModalClose />
                <DialogTitle>Merge Company</DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Typography level="body-sm">
                            Merge <strong>{source?.name_display}</strong> into another company. All facts will be reassigned.
                        </Typography>
                        <Input placeholder="Search target company..." value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
                        <Stack gap={0.5} sx={{ maxHeight: 280, overflowY: 'auto' }}>
                            {filtered.slice(0, 50).map((c) => (
                                <Button
                                    key={c.id}
                                    variant={selectedId === c.id ? 'solid' : 'plain'}
                                    color={selectedId === c.id ? 'primary' : 'neutral'}
                                    size="sm"
                                    onClick={() => setSelectedId(c.id)}
                                    sx={{ justifyContent: 'flex-start' }}
                                >
                                    {c.name_display}
                                </Button>
                            ))}
                            {filtered.length === 0 && (
                                <Typography level="body-xs" color="neutral">
                                    No companies match
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" color="neutral" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button color="danger" disabled={!selectedId} loading={loading} onClick={handleMerge}>
                        Merge
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
